import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styles from './MobileInfoPanel.module.css';

/**
 * MobileInfoPanel - A floating info button with slide-in drawer panel
 * 
 * Features:
 * - Fixed position circular button in bottom-right corner
 * - Respects iOS safe-area-inset-bottom
 * - Slide-in drawer from the right side
 * - Links to About, Contact, Privacy, and Terms pages
 * - Closes on: backdrop click, link click, or Escape key
 * - Prevents background scroll when open
 * - Fully accessible with ARIA attributes
 */
export default function MobileInfoPanel() {
  const [isOpen, setIsOpen] = useState(false);

  // Open drawer
  const openDrawer = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Close drawer
  const closeDrawer = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Safari back-forward cache fix - always close drawer on page restore
  useEffect(() => {
    const handlePageShow = (e) => {
      // Force close on iOS Safari restore, especially when from BFCache
      setIsOpen(false);
    };

    // Run on mount to ensure clean state
    handlePageShow();
    
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Handle Escape key to close drawer
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeDrawer();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, closeDrawer]);

  // Prevent background scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      // Store original overflow value
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    // Only close if clicking the backdrop itself, not its children
    if (e.target === e.currentTarget) {
      closeDrawer();
    }
  }, [closeDrawer]);

  // Handle link click - close drawer when navigating
  const handleLinkClick = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  return (
    <>
      {/* Floating Info Button */}
      <button
        className={`${styles.infoButton} ${isOpen ? styles.infoButtonOpen : ''}`}
        onClick={openDrawer}
        aria-label="Open information menu"
        aria-expanded={isOpen}
        aria-controls="mobile-info-drawer"
        title="Info"
      >
        <svg
          className={styles.infoIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </button>

      {/* Backdrop - only render when open */}
      {isOpen && (
        <div
          className={styles.backdrop}
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      {/* Slide-in Drawer - only render when open to prevent flash */}
      {isOpen && (
        <aside
          id="mobile-info-drawer"
          className={`${styles.drawer} ${styles.drawerOpen}`}
          role="dialog"
          aria-label="Information menu"
          aria-hidden={false}
        >
        {/* Header with Close Button */}
        <header className={styles.drawerHeader}>
          <h2 className={styles.drawerTitle}>About DocuHounds</h2>
          <button
            className={styles.closeBtn}
            onClick={closeDrawer}
            aria-label="Close information menu"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" fill="none" stroke="#334155" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>
        </header>

        {/* Navigation Links */}
        <nav className={styles.drawerNav} aria-label="Information pages">
          <Link
            to="/m/about"
            className={styles.navLink}
            onClick={handleLinkClick}
          >
            <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <span>About Us</span>
            <svg className={styles.navChevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
          <Link
            to="/m/contact"
            className={styles.navLink}
            onClick={handleLinkClick}
          >
            <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <span>Contact</span>
            <svg className={styles.navChevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
          <Link
            to="/m/blog"
            className={styles.navLink}
            onClick={handleLinkClick}
          >
            <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
            <span>Blog</span>
            <svg className={styles.navChevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
          <Link
            to="/m/privacy"
            className={styles.navLink}
            onClick={handleLinkClick}
          >
            <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>Privacy Policy</span>
            <svg className={styles.navChevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
          <Link
            to="/m/terms"
            className={styles.navLink}
            onClick={handleLinkClick}
          >
            <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <line x1="10" y1="9" x2="8" y2="9" />
            </svg>
            <span>Terms of Service</span>
            <svg className={styles.navChevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
        </nav>
      </aside>
      )}
    </>
  );
}
